import React, { useMemo, useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import { useSafetyReadModel } from './use-safety-read-model.js';
import SafetyContextRail from './SafetyContextRail.js';
import SafetyMeetingHistory from './SafetyMeetingHistory.js';
import SafetyMeetingDetailDrawer from './SafetyMeetingDetailDrawer.js';
import SafetyMeetingKiosk from './SafetyMeetingKiosk.js';
import SafetyMeetingOpen from './SafetyMeetingOpen.js';
import Drawer from '../../shared/ui/Drawer.js';

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function presetToRange(preset: string, customStart: string, customEnd: string) {
  if (preset === 'custom') {
    return { startDate: customStart || '', endDate: customEnd || '' };
  }
  if (preset === 'all') {
    return { startDate: '', endDate: '' };
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : preset === '90d' ? 90 : 0;
  if (days === 0) return { startDate: '', endDate: '' };

  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - days);
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

export default function SafetyModule() {
  const [terminalFilter, setTerminalFilter] = useState('all');
  const [rangePreset, setRangePreset] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | number | null>(null);
  const [showOpenMeeting, setShowOpenMeeting] = useState(false);
  const [kioskMeetingId, setKioskMeetingId] = useState<string | number | null>(null);

  const { startDate, endDate } = useMemo(
    () => presetToRange(rangePreset, customStart, customEnd),
    [rangePreset, customStart, customEnd]
  );

  const {
    meetings,
    total,
    terminalOptions,
    summary,
    loading,
    error,
    hasAccess,
    refreshSafety,
  } = useSafetyReadModel(terminalFilter, startDate, endDate);

  function handleResetFilters() {
    setRangePreset('all');
    setCustomStart('');
    setCustomEnd('');
  }

  function closeOpenMeeting() { setShowOpenMeeting(false); }

  const openCount = summary?.open ?? 0;

  return (
    <div>
      {hasAccess && (
        <SafetyContextRail
          terminalFilter={terminalFilter}
          terminalOptions={terminalOptions}
          rangePreset={rangePreset}
          startDate={rangePreset === 'custom' ? customStart : startDate}
          endDate={rangePreset === 'custom' ? customEnd : endDate}
          summary={summary}
          onTerminalChange={setTerminalFilter}
          onRangePresetChange={setRangePreset}
          onStartDateChange={setCustomStart}
          onEndDateChange={setCustomEnd}
          onRefresh={refreshSafety}
          onResetFilters={handleResetFilters}
        />
      )}

      {hasAccess && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowOpenMeeting(true)}
          >
            + Open meeting
            {openCount > 0 ? (
              <span style={{ marginLeft: 6, color: C.navy, fontWeight: 700 }}>· {openCount} open</span>
            ) : null}
          </button>
        </div>
      )}

      <SafetyMeetingHistory
        meetings={meetings}
        total={total}
        loading={loading}
        error={error}
        hasAccess={hasAccess}
        onSelectMeeting={setSelectedMeetingId}
      />

      <SafetyMeetingDetailDrawer
        meetingId={selectedMeetingId}
        onClose={() => setSelectedMeetingId(null)}
        onEnterKiosk={(meetingId) => {
          setSelectedMeetingId(null);
          setKioskMeetingId(meetingId);
        }}
        onMeetingClosed={() => {
          refreshSafety();
          setSelectedMeetingId(null);
          setKioskMeetingId(null);
        }}
      />

      <Drawer
        open={showOpenMeeting}
        onClose={closeOpenMeeting}
        title="Open a safety meeting"
        width={520}
      >
        <SafetyMeetingOpen
          terminalOptions={terminalOptions}
          onSuccess={(meetingId) => {
            refreshSafety();
            closeOpenMeeting();
            if (meetingId) setSelectedMeetingId(meetingId);
          }}
        />
      </Drawer>

      {kioskMeetingId != null && (
        <SafetyMeetingKiosk
          meetingId={kioskMeetingId}
          onExit={() => setKioskMeetingId(null)}
          onMeetingClosed={() => {
            refreshSafety();
            setKioskMeetingId(null);
            setSelectedMeetingId(null);
          }}
        />
      )}
    </div>
  );
}
